import { FloatingActionBar } from '@dev-spendesk/grapes';
import { useMemo, useState } from 'react';

import { useModal as useModalGrapes } from 'common/hooks/useModalGrapes';
import { unwrapQuery } from 'src/core/api/unwrapQuery';
import { BetaFeatureNotice } from 'src/core/common/components/BetaFeatureNotice';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { useOrganisationCompanyList } from 'src/core/modules/organisation-reporting/hooks/data/useOrganisationCompanyList';

import type { FilterTypeOption } from '../../filter-type';
import { useOrganisationMembers } from '../../hooks/useOrganisationMembers';
import { useOrganisationSettings } from '../../hooks/useOrganisationSettings';
import { type Setting } from '../../setting';
import { CostCenterAssignmentEditModal } from '../CostCenterAssignmentEditModal';
import { CostCenterDeleteModal } from '../CostCenterDeleteModal';
import { CostCenterDetailsEditModal } from '../CostCenterDetailsEditModal';
import { CostCenterMergeModal } from '../CostCenterMergeModal';
import { CostCenterPageHeader } from '../CostCenterPageHeader';
import { OrganisationSettingsModal } from '../OrganisationSettingsModal';
import { SettingsTable } from '../SettingsTable';

export const OrganisationCostCentersPage = () => {
  const { t } = useTranslation('global');
  const settings = useOrganisationSettings();
  const companies = useOrganisationCompanyList();
  const organisationMembers = useOrganisationMembers();
  const members = unwrapQuery(organisationMembers);
  const [typeFilter, setTypeFilter] = useState<FilterTypeOption>('');
  const [entityFilters, setEntityFilters] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRows, setSelectedRows] = useState<Setting[]>([]);

  const filteredSettings: Setting[] = useMemo(() => {
    let newFilteredSettings = settings.data ?? [];

    const getOwnerName = (ownerId: string) => {
      const owner = members?.find((member) => member.id === ownerId);
      if (!owner) {
        return '';
      }
      if (owner.isPending) {
        return owner.email;
      }
      return owner.fullname;
    };

    if (searchTerm) {
      newFilteredSettings = newFilteredSettings.filter((setting) => {
        const ownerName = getOwnerName(setting.ownerId).toLowerCase();
        return (
          setting.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          ownerName.includes(searchTerm.toLowerCase())
        );
      });
    }

    if (typeFilter) {
      if (typeFilter === 'cross-entity') {
        newFilteredSettings = newFilteredSettings.filter(
          (setting) => setting.companyIds.length > 1,
        );
      } else {
        newFilteredSettings = newFilteredSettings.filter(
          (setting) => setting.companyIds.length <= 1,
        );
      }
    }

    if (entityFilters.length > 0) {
      newFilteredSettings = newFilteredSettings.filter((setting) =>
        setting.companyIds.some((companyId) =>
          entityFilters.includes(companyId),
        ),
      );
    }

    return newFilteredSettings;
  }, [settings.data, searchTerm, typeFilter, entityFilters, members]);

  const [creationModal, showCreationModal] = useModalGrapes<{
    openedFromCreationSelectionModal?: boolean;
  }>(({ isOpen, onClose }) => {
    return <OrganisationSettingsModal isOpen={isOpen} onCancel={onClose} />;
  });

  const [mergeModal, showMergeModal] = useModalGrapes<{
    selectedSettings: Setting[];
  }>(({ isOpen, onClose, selectedSettings }) => {
    if (!isOpen) {
      return <></>;
    }
    return (
      <CostCenterMergeModal
        isOpen={isOpen}
        onCancel={() => {
          setSelectedRows([]);
          onClose();
        }}
        settings={selectedSettings}
      />
    );
  });

  const [editCostCenterModal, showEditCostCenterModal] = useModalGrapes<{
    setting: Setting;
  }>(({ isOpen, onClose, setting }) => {
    if (!isOpen) {
      return <></>;
    }
    return (
      <CostCenterDetailsEditModal
        setting={setting}
        isOpen={isOpen}
        onCancel={onClose}
      />
    );
  });

  const [assignmentModal, showAssignmentModal] = useModalGrapes<{
    setting: Setting;
  }>(({ isOpen, onClose, setting }) => {
    return (
      <CostCenterAssignmentEditModal
        setting={setting}
        isOpen={isOpen}
        mode="singleModal"
        onCancel={onClose}
      />
    );
  });

  const [deleteCostCenterModal, showDeleteCostCenterModal] = useModalGrapes<{
    selectedSettings: Setting[];
  }>(({ isOpen, onClose, selectedSettings }) => {
    if (!selectedSettings || selectedSettings.length === 0 || !isOpen) {
      return <></>;
    }
    return (
      <CostCenterDeleteModal
        settings={selectedSettings}
        isOpen={isOpen}
        onCancel={() => {
          setSelectedRows([]);
          onClose();
        }}
      />
    );
  });

  const hasSharedCompanySelection = useMemo(
    () =>
      selectedRows.some((s1, index) =>
        selectedRows
          .slice(index + 1)
          .some((s2) =>
            s1.companyIds.some((id1) => s2.companyIds.includes(id1)),
          ),
      ),
    [selectedRows],
  );

  const actionBar = () => {
    return (
      <FloatingActionBar
        actions={[
          {
            text: t('multiEntityHub.costCenters.table.bulkMergeButton'),
            onClick: () => {
              showMergeModal({ selectedSettings: selectedRows });
            },
            iconName: 'arrow-merge',
            tooltipContent: hasSharedCompanySelection
              ? t('multiEntityHub.costCenters.tooltips.sharedCompany')
              : '',
            isDisabled: hasSharedCompanySelection,
          },
          {
            text: t('multiEntityHub.costCenters.table.bulkDeleteButton'),
            iconName: 'trash',
            onClick: () => {
              showDeleteCostCenterModal({ selectedSettings: selectedRows });
            },
          },
        ]}
      >
        {t('members.table.selectedMembers', {
          count: selectedRows.length,
        })}
      </FloatingActionBar>
    );
  };

  return (
    <>
      {selectedRows.length > 1 && actionBar()}
      {mergeModal}
      {creationModal}
      {editCostCenterModal}
      {assignmentModal}
      {deleteCostCenterModal}
      <div className="flex flex-col gap-24">
        <CostCenterPageHeader
          onAddCostCenter={showCreationModal}
          onSearch={setSearchTerm}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          entityFilters={entityFilters}
          setEntityFilters={setEntityFilters}
          companies={companies}
        />

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-content-primary heading-l">
              {t('multiEntityHub.costCenters.title')}
            </h2>
            <p className="mt-4 text-content-primary body-m">
              {t('multiEntityHub.costCenters.subtitle', {
                count: settings.data?.length ?? 0,
              })}
            </p>
          </div>
          <BetaFeatureNotice
            featureName={FEATURES.MULTI_ENTITY_CONFIG}
            feedbackTags={['multi-entity-config']}
            showOnlyFeedbackModal
            label={t('misc.beta')}
          />
        </div>

        <SettingsTable
          settings={filteredSettings}
          onEditCostCenter={(setting) => showEditCostCenterModal({ setting })}
          onAssignCompanies={(setting) => showAssignmentModal({ setting })}
          onDeleteCostCenter={(setting) => {
            showDeleteCostCenterModal({ selectedSettings: [setting] });
          }}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
        />
      </div>
    </>
  );
};
